import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { useCallback, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import {
    formatCurrencyToShow as formatCurrency,
    parseCurrencyToNumber,
} from "../../helpers/currency";
import { findAcquirer } from "../../helpers/customer";
import { percentageMask } from "../../helpers/masks/number";
import { useQuery } from "../../hooks/useQuery";
import { Acquirer } from "../../interfaces/acquirer";
import { Customer } from "../../interfaces/customer";
import { ApiResponse } from "../../interfaces/paginated";
import { api } from "../../services/api";
import { customerAcquirerSchema } from "../../validators/schemas/customerAcquirer";

type FormData = {
    pixAcquirer?: string;
    pixFeePercentage?: string;
    pixFixedFee?: string;
    cardAcquirer?: string;
    cardFeePercentage?: string;
    cardFixedFee?: string;
};

interface AcquirerEditModalProps {
    open: boolean;
    onClose: () => void;
    data: Customer | null;
    onSuccess?: (data: FormData) => void;
    onError?: (data: FormData, error: string) => void;
}

function FeeAdornment({ value }: { value?: string }) {
    return <InputAdornment position="end">{value}</InputAdornment>;
}

export function AcquirerEditModal({
    open,
    onClose,
    data,
    onSuccess,
    onError,
}: AcquirerEditModalProps) {
    const { data: acquirers, isLoading } = useQuery<Acquirer[]>(
        "/acquirers?type=gateway&active=1"
    );

    const {
        handleSubmit,
        control,
        setValue,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<FormData>({
        resolver: yupResolver(customerAcquirerSchema),
    });

    const renderAcquirersList = useCallback(() => {
        return acquirers?.map((item) => (
            <MenuItem key={item.id} value={item.id}>
                {item.name}
            </MenuItem>
        ));
    }, [acquirers]);

    const handleClose = () => {
        onClose();
        reset();
    };

    const onSubmit = async (values: FormData) => {
        try {
            const payload = {
                acquirers: [
                    {
                        acquirer_id: values.pixAcquirer,
                        payment_method: "pix",
                        fee_percentage: Number(values.pixFeePercentage),
                        fixed_fee: values.pixFixedFee
                            ? parseCurrencyToNumber(values.pixFixedFee)
                            : null,
                    },
                    {
                        acquirer_id: values.cardAcquirer,
                        payment_method: "card",
                        fee_percentage: Number(values.cardFeePercentage),
                        fixed_fee: values.cardFixedFee
                            ? parseCurrencyToNumber(values.cardFixedFee)
                            : null,
                    },
                ],
            };

            const response = await api.put<ApiResponse<null>>(
                `/customers/${data?.id}/acquirers`,
                payload
            );

            if (!response.data.status) {
                onError?.(values, response.data.message);
                return;
            }

            onSuccess?.(values);
        } catch (error: any) {
            onError?.(values, error?.response?.data?.message || error?.message);
        } finally {
            handleClose();
        }
    };

    useEffect(() => {
        if (data) {
            const paymentMethods = ["pix", "card"] as const;

            paymentMethods.forEach((method) => {
                const acquirer = findAcquirer(data, method);
                const customerAcquirer = acquirer?.customer_acquirer;

                setValue(`${method}Acquirer`, acquirer?.id || "");
                setValue(
                    `${method}FeePercentage`,
                    percentageMask(customerAcquirer?.fee_percentage)
                );
                setValue(
                    `${method}FixedFee`,
                    formatCurrency(customerAcquirer?.fixed_fee) || ""
                );
            });
        }

        return () => reset();
    }, [data]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            disableRestoreFocus
        >
            <DialogTitle variant="h6" component="h2">
                {data?.name} - {data?.project_name}
            </DialogTitle>

            <DialogContent>
                <Box
                    gap={2}
                    display="flex"
                    flexDirection="column"
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                    noValidate
                >
                    <Typography variant="subtitle2" fontWeight={600} mt={1}>
                        Configuração PIX
                    </Typography>

                    <FormControl fullWidth size="small">
                        <InputLabel>Adquirente PIX</InputLabel>
                        <Controller
                            name="pixAcquirer"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <Select
                                    disabled={isLoading}
                                    label="Adquirente PIX"
                                    placeholder={
                                        isLoading ? "Carregando..." : ""
                                    }
                                    error={!!errors.pixAcquirer}
                                    {...field}
                                >
                                    {renderAcquirersList()}
                                </Select>
                            )}
                        />
                        <FormHelperText error={!!errors.pixAcquirer}>
                            {errors.pixAcquirer?.message}
                        </FormHelperText>
                    </FormControl>

                    <Box
                        display="flex"
                        gap={2}
                        flexDirection={{ xs: "column", sm: "row" }}
                    >
                        <Controller
                            name="pixFeePercentage"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, ...field } }) => (
                                <TextField
                                    label="Taxa PIX (%)"
                                    size="small"
                                    fullWidth
                                    placeholder="Ex: 1.3"
                                    error={!!errors.pixFeePercentage}
                                    helperText={
                                        errors.pixFeePercentage?.message
                                    }
                                    InputProps={{
                                        endAdornment: (
                                            <FeeAdornment value="%" />
                                        ),
                                    }}
                                    onChange={(e) =>
                                        onChange(percentageMask(e.target.value))
                                    }
                                    {...field}
                                />
                            )}
                        />

                        <Controller
                            name="pixFixedFee"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, ...field } }) => (
                                <TextField
                                    label="Taxa Fixa PIX"
                                    size="small"
                                    fullWidth
                                    placeholder="R$ 0.30"
                                    error={!!errors.pixFixedFee}
                                    helperText={errors.pixFixedFee?.message}
                                    InputProps={{
                                        endAdornment: (
                                            <FeeAdornment value="R$" />
                                        ),
                                    }}
                                    onChange={({ target: { value } }) => {
                                        const mv = formatCurrency(value, true);
                                        onChange(mv);
                                    }}
                                    {...field}
                                />
                            )}
                        />
                    </Box>

                    <Typography variant="subtitle2" fontWeight={600} mt={1}>
                        Configuração Cartão
                    </Typography>

                    <FormControl fullWidth size="small">
                        <InputLabel>Adquirente Cartão</InputLabel>
                        <Controller
                            name="cardAcquirer"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <Select
                                    disabled={isLoading}
                                    label="Adquirente Cartão"
                                    placeholder={
                                        isLoading ? "Carregando..." : ""
                                    }
                                    error={!!errors.cardAcquirer}
                                    {...field}
                                >
                                    {renderAcquirersList()}
                                </Select>
                            )}
                        />
                        <FormHelperText error={!!errors.cardAcquirer}>
                            {errors.cardAcquirer?.message}
                        </FormHelperText>
                    </FormControl>

                    <Box
                        display="flex"
                        gap={2}
                        flexDirection={{ xs: "column", sm: "row" }}
                    >
                        <Controller
                            name="cardFeePercentage"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, ...field } }) => (
                                <TextField
                                    label="Taxa Cartão (%)"
                                    size="small"
                                    fullWidth
                                    placeholder="Ex: 2.5"
                                    error={!!errors.cardFeePercentage}
                                    helperText={
                                        errors.cardFeePercentage?.message
                                    }
                                    InputProps={{
                                        endAdornment: (
                                            <FeeAdornment value="%" />
                                        ),
                                    }}
                                    onChange={(e) =>
                                        onChange(percentageMask(e.target.value))
                                    }
                                    {...field}
                                />
                            )}
                        />

                        <Controller
                            name="cardFixedFee"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, ...field } }) => (
                                <TextField
                                    label="Taxa Fixa Cartão"
                                    size="small"
                                    fullWidth
                                    placeholder="R$ 0.30"
                                    error={!!errors.cardFixedFee}
                                    helperText={errors.cardFixedFee?.message}
                                    InputProps={{
                                        endAdornment: (
                                            <FeeAdornment value="R$" />
                                        ),
                                    }}
                                    onChange={({ target: { value } }) => {
                                        const mv = formatCurrency(value, true);
                                        onChange(mv);
                                    }}
                                    {...field}
                                />
                            )}
                        />
                    </Box>

                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={1}
                        mt={2}
                    >
                        <Button onClick={handleClose} color="inherit">
                            Cancelar
                        </Button>
                        <LoadingButton
                            variant="contained"
                            type="submit"
                            loading={isSubmitting}
                        >
                            Salvar
                        </LoadingButton>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
